import { Component, OnInit, signal } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Router, RouterModule } from '@angular/router';
import { map, Observable } from 'rxjs';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { matchPasswordValidator } from '../../../../directives/match-password.directive';
import { passwordValidator } from '../../../../directives/password.directive';
import { LayoutService } from '../../service/layout.service';
import { AuthService } from '../../../../../core/services/auth.service';
import { TenantService } from '../../../../../core/services/tenant.service';
import { ToastService } from '../../../../../core/services/toast.service';
import { Severity } from '../../../../../core/enums/toast-severity.enum';
import { ErrorMessageComponent } from '../../../../components/error-message/error-message.component';
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { Logo } from '../../../../components/icons/logo';
import { StyleClassModule } from 'primeng/styleclass';
import { CommonModule } from '@angular/common';
import { SelectModule } from 'primeng/select';
import { DividerModule } from 'primeng/divider';
import { UserService } from '../../../../../core/services/user.service';
import { NotificationBellComponent } from '../../../../components/notification-bell/notification-bell.component';
import { ApprovalWorkflowService } from '../../../../../core/services/approval-workflow.service';

@Component({
    selector: 'app-topbar',
    templateUrl: './app-topbar.component.html',
    imports: [RouterModule, CommonModule, StyleClassModule, DividerModule, Logo, MenuModule, DialogModule, ButtonModule, InputTextModule, FormsModule, ToastModule, ErrorMessageComponent, ReactiveFormsModule, SelectModule, NotificationBellComponent, TooltipModule],
    styleUrls: ['./app-topbar.component.scss']
})
export class AppTopbar implements OnInit {
    menuItems!: MenuItem[];
    tenantLogoUrl$: Observable<string | undefined>;
    changePasswordDialog = false;
    isChangingPassword = false;
    userProfileDialog = false;
    transferRoleDialog = false;
    isTransferRole = false;
    employees: any[] = [];
    currentUser: any;
    tenantId: string | null = null;
    selectedEmployee: any;
    firstName = '';
    lastName = '';
    pendingApprovalCount = 0;

    changePasswordForm = new FormGroup(
        {
            currentPassword: new FormControl('', [Validators.required]),
            newPassword: new FormControl('', [Validators.required, passwordValidator()]),
            confirmPassword: new FormControl('')
        },
        { validators: matchPasswordValidator('newPassword', 'confirmPassword') }
    );

    transferRoleForm = new FormGroup({
        password: new FormControl('', [Validators.required]),
        employee: new FormControl<any>(null, [Validators.required])
    });

    constructor(
        public layoutService: LayoutService,
        private readonly authService: AuthService,
        private readonly router: Router,
        private readonly tenantService: TenantService,
        private readonly toastService: ToastService,
        private readonly userService: UserService,
        private readonly approvalWorkflowService: ApprovalWorkflowService
    ) {
        this.tenantLogoUrl$ = this.getTenantLogoUrl();
        this.currentUser = this.authService.getCurrentUser();
    }

    ngOnInit(): void {
        this.loadTenantProfile();
        this.initializeMenuItems();
        this.loadCurrentUser();
        this.loadPendingApprovalCount();
    }

    private loadPendingApprovalCount(): void {
        this.approvalWorkflowService.getPendingApprovalCount().subscribe({
            next: (res) => {
                this.pendingApprovalCount = res.isSuccess ? (res.data ?? 0) : 0;
            },
            error: () => {
                this.pendingApprovalCount = 0;
            }
        });
    }
    private loadEmployees(): void {
        this.userService.getUsersByTenant().subscribe({
            next: (response) => {
                if (response.isSuccess) {
                    this.employees = response?.data || [];
                } else {
                    this.toastService.error(response.errors?.[0] || 'Failed to load employees', Severity.ERROR);
                }
            },
            error: (error) => {
                console.error('Failed to load employees:', error);
                this.toastService.error('Failed to load employees', Severity.ERROR);
            }
        });
    }

    loadEmployeeDetails(): void {
        this.selectedEmployee = this.transferRoleForm.get('employee')?.value;
    }

    private getTenantLogoUrl(): Observable<string | undefined> {
        return this.tenantService.currentTenant$.pipe(
            map((tenant) => tenant?.logoUrl),
            map((url) => (url === null ? undefined : url))
        );
    }

    private loadTenantProfile(): void {
        this.tenantService.loadTenantProfile().catch((error) => {
            console.error('Failed to load tenant profile:', error);
        });
    }

    private loadCurrentUser(): void {
        this.authService
            .loadCurrentUserDetails()
            .then(() => {
                this.currentUser = this.authService.getCurrentUser();
                this.tenantId = this.currentUser?.userLoginInfo?.tenantId || null;
            })
            .catch((error) => {
                console.error('Failed to load current user profile:', error);
            });
    }

    private initializeMenuItems(): void {
        const items: MenuItem[] = [
            {
                label: 'User Profile',
                icon: 'pi pi-user',
                command: () => this.showUserProfileDialog()
            },
            {
                label: 'Change Password',
                icon: 'pi pi-key',
                command: () => this.showChangePasswordDialog()
            },
            // {
            //     label: 'Transfer Role',
            //     icon: 'pi pi-sync',
            //     command: () => this.showTransferRoleDialog(),
            //     visible: this.isTenantAdmin
            // },
            { separator: true },
            {
                label: 'Sign Out',
                icon: 'pi pi-sign-out',
                command: () => this.logOut()
            }
        ];
        // Filter out items that are not visible (if 'visible' is explicitly false)
        this.menuItems = items.filter((item) => item.visible !== false);
    }

    public get roleNames(): string {
        return this.currentUser?.roles?.map((role: any) => role.roleName).join(',') || '';
    }

    /** Same role-priority-based landing page used right after login, so the logo always goes "home" for this user instead of the general Dashboard. */
    public get homeLink(): string {
        return this.currentUser?.index || '/welcome';
    }

    public get isTenantAdmin(): boolean {
        return this.currentUser?.roles?.some((role: any) => role.roleName === 'Admin') || false;
    }

    toggleDarkMode(): void {
        this.layoutService.layoutConfig.update((state) => ({
            ...state,
            darkTheme: !state.darkTheme
        }));
    }

    logOut(): void {
        this.authService.logout();
        this.router.navigate(['/login']);
    }

    goToReportProgress(): void {
        this.router.navigate(['/reports/progress']);
    }

    goToMyApprovals(): void {
        this.router.navigate(['/my-approvals']);
    }

    showChangePasswordDialog(): void {
        this.changePasswordDialog = true;
        this.changePasswordForm.reset();
    }

    showTransferRoleDialog(): void {
        this.transferRoleDialog = true;
        this.transferRoleForm.reset();
        this.selectedEmployee = null;
        this.loadEmployees();
        this.tenantService.verifyTransferRoleToken(this.currentUser?.id ?? '').subscribe({
            next: (response) => {
                if (response.isSuccess) {
                    this.selectedEmployee = this.employees.find((d) => d.userId === response?.data?.employee?.userId);
                    console.log('Selected Employee:', this.selectedEmployee);
                    this.transferRoleForm.patchValue({
                        password: '',
                        employee: this.selectedEmployee
                    });
                    this.transferRoleForm.get('employee')?.disable();
                    this.isTransferRole = true;
                } else {
                    console.log(response.errors?.[0] || 'Failed to verify transfer role token');
                }
            },
            error: (error) => {
                console.error('Failed to verify transfer role token:', error);
                this.toastService.error('Failed to verify transfer role token', Severity.ERROR);
            }
        });
    }

    showUserProfileDialog(): void {
        this.userProfileDialog = true;
        this.loadProfileName();
    }

    /** Loads the tenant admin's first/last name from their profile so the dialog matches the Update Admin Profile form. */
    private loadProfileName(): void {
        const tenantId = this.tenantId ?? this.currentUser?.userLoginInfo?.tenantId;
        if (!tenantId) {
            return;
        }
        this.tenantService.getTenantById(tenantId).subscribe({
            next: (response) => {
                if (response.isSuccess && response.data) {
                    const adminUser = response.data.tenantUserList?.find((user: any) => user.isTenantAdmin);
                    this.firstName = adminUser?.userProfile?.firstName ?? '';
                    this.lastName = adminUser?.userProfile?.lastName ?? '';
                }
            },
            error: (error) => {
                console.error('Failed to load user profile details:', error);
            }
        });
    }

    changePassword(): void {
        if (!this.changePasswordForm.valid) {
            this.toastService.error('Please enter a valid new password and confirm password.', Severity.ERROR);
            return;
        }

        if (this.changePasswordForm.hasError('passwordMismatch')) {
            this.toastService.error('Passwords do not match.', Severity.ERROR);
            return;
        }

        this.isChangingPassword = true;

        const { currentPassword, newPassword } = this.changePasswordForm.value;

        this.authService.changePassword(currentPassword ?? '', newPassword ?? '').subscribe({
            next: (response) => this.handlePasswordChangeResponse(response),
            error: () => this.handlePasswordChangeError(),
            complete: () => (this.isChangingPassword = false)
        });
    }

    transferRole(): void {
        console.log('Transfer Role Form Value:', this.transferRoleForm.value);
        if (!this.transferRoleForm.valid) {
            this.transferRoleForm.markAllAsTouched();
            return;
        }

        this.transferRoleDialog = false;
        this.transferRoleForm.get('employee')?.enable();
        const { password, employee } = this.transferRoleForm.value;
        const payload = {
            fromUserId: this.currentUser?.id,
            password: password ?? '',
            toUserId: employee?.id
        };

        if (this.isTransferRole) {
            this.tenantService
                .cancelTransferRole({
                    userId: this.currentUser?.id ?? '',
                    password: password ?? ''
                })
                .subscribe({
                    next: (response) => {
                        if (response.isSuccess) {
                            this.toastService.success('Role transfer cancelled successfully.');
                            this.transferRoleDialog = false;
                            this.transferRoleForm.reset();
                            this.selectedEmployee = null;
                            this.isTransferRole = false;
                        } else {
                            this.toastService.error(response.errors?.[0] || 'Failed to cancel role transfer', Severity.ERROR);
                        }
                    },
                    error: () => {
                        this.toastService.error('Failed to cancel role transfer. Please try again.', Severity.ERROR);
                    }
                });
            return;
        }
        this.tenantService.transferTenantOwnership(payload).subscribe({
            next: (response) => {
                if (response.isSuccess) {
                    this.toastService.success('Role transferred successfully.');
                    this.transferRoleDialog = false;
                    this.transferRoleForm.reset();
                    this.selectedEmployee = null;
                } else {
                    this.toastService.error(response.errors?.[0] || 'Failed to transfer role', Severity.ERROR);
                }
            },
            error: () => {
                this.toastService.error('Failed to transfer role. Please try again.', Severity.ERROR);
            },
            complete: () => (this.isTransferRole = false)
        });
    }

    private handlePasswordChangeResponse(response: any): void {
        if (response.isSuccess) {
            this.toastService.success('Password changed successfully.');
            this.changePasswordDialog = false;
            this.changePasswordForm.reset();
        } else {
            this.toastService.error(response.errors?.[0] || 'Failed to change password', Severity.ERROR);
        }
    }

    private handlePasswordChangeError(): void {
        this.toastService.error('Failed to change password. Please try again.', Severity.ERROR);
    }

    isInvalid(controlName: string): boolean {
        const control = this.changePasswordForm.get(controlName);
        return !!(control && control.invalid && control.touched);
    }

    isInvalidTransferRole(controlName: string): boolean {
        const control = this.transferRoleForm.get(controlName);
        return !!(control && control.invalid && (control.dirty || control.touched));
    }

    updateProfile(): void {
        this.router.navigate([`/tenant/edit`], { state: { id: this.tenantId } });
        this.userProfileDialog = false;
    }
}
